import { NavLink } from 'react-router-dom';
import {
  CalendarDays,
  FileSpreadsheet,
  LayoutDashboard,
  Package,
  ScrollText,
  Settings,
  ShieldCheck,
  Table2,
  Upload,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { hasPermission, type PermissionKey } from '@afiliados/shared';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { FenixLogo } from '@/components/FenixLogo';
import { useAuthStore } from '@/features/auth/store';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: PermissionKey;
}

const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Faturamento',
    items: [
      { to: '/dashboard', label: 'Painel', icon: LayoutDashboard, permission: 'dashboard.read' },
      { to: '/mensal', label: 'Mensal', icon: Table2, permission: 'reports.read' },
      { to: '/diario', label: 'Diário', icon: CalendarDays, permission: 'reports.read' },
      { to: '/afiliados', label: 'Afiliados', icon: Users, permission: 'reports.read' },
      { to: '/produtos', label: 'Produtos', icon: Package, permission: 'reports.read' },
    ],
  },
  {
    label: 'Dados',
    items: [
      { to: '/importacoes', label: 'Importações', icon: Upload, permission: 'imports.read' },
      { to: '/configuracoes', label: 'Configurações', icon: Settings, permission: 'settings.read' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: UserCog, permission: 'users.read' },
      { to: '/perfis', label: 'Perfis e permissões', icon: ShieldCheck, permission: 'roles.read' },
      { to: '/auditoria', label: 'Auditoria', icon: ScrollText, permission: 'audit.read' },
    ],
  },
];

export function AppSidebar() {
  const user = useAuthStore((s) => s.user);
  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.permission || hasPermission(user, i.permission)),
  })).filter((g) => g.items.length);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <FenixLogo className="size-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Fênix</span>
                  <span className="truncate text-xs text-muted-foreground">Afiliados</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <NavLink to={item.to}>
                      {({ isActive }) => (
                        <SidebarMenuButton isActive={isActive} tooltip={item.label}>
                          <item.icon />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <p className="flex items-center gap-1 px-2 text-[10px] text-muted-foreground group-data-[collapsible=icon]:hidden">
          <FileSpreadsheet className="size-3" /> v0.1.0 · Grupo Fênix
        </p>
      </SidebarFooter>
    </Sidebar>
  );
}
